import { useState, useEffect } from 'react';
import { MoneyInput } from '../../components/ui/money-input';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Save,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Trash2,
  ListChecks,
  Ban,
  Euro,
  MapPin,
  ShieldCheck,
  Award,
  CalendarClock,
  StickyNote,
} from 'lucide-react';
import { CollapsibleCard } from '../../components/ui/collapsible-card';
import { api } from '../../core/apiClient';

// Cada grup representa un tipus de producte/servei (Pumptrack, Skatepark...) i té els seus PROPIS
// criteris d'import, proximitat, solvència i adjudicació — a diferència de les paraules d'exclusió i
// el termini d'execució, que són comuns a tots els grups (vegeu més avall) i sempre prevalen per sobre
// d'aquests valors particulars.
interface KeywordGroup {
  name: string;
  es: string[];
  ca: string[];
  importMin?: number | null;
  importMax?: number | null;
  proximityRegions?: string[];
  proximityMaxHours?: number | null;
  companySolvencyEconomic?: number | null;
  companySolvencyTechnical?: number | null;
  adjudicationMinPriceWeight?: number | null;
  notes?: string | null;
}

interface CriteriaConfigData {
  adequacyKeywordGroups?: KeywordGroup[];
  exclusionKeywordsEs?: string[];
  exclusionKeywordsCa?: string[];
  executionMaxMonths?: number | null;
}

function listToText(list?: string[]): string {
  return (list || []).join(', ');
}

function textToList(text: string): string[] {
  return text
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

const inputCls = 'w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';
const subheaderCls = 'flex items-center gap-1.5 text-xs font-semibold text-gray-700 pt-1';

// Input de paraules clau en forma de "chips": s'afegeixen d'una en una (Enter o botó) i cada una es
// pot eliminar amb la seva creueta. Evita l'edició incòmoda d'una llista separada per comes en un
// únic camp de text.
function KeywordChipsInput({
  value,
  onChange,
  placeholder,
  locale,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  locale: string;
}) {
  const [draft, setDraft] = useState('');

  const addWord = () => {
    const word = draft.trim();
    if (!word) return;
    if (!value.some((w) => w.toLowerCase() === word.toLowerCase())) {
      onChange([...value, word].sort((a, b) => a.localeCompare(b, locale)));
    }
    setDraft('');
  };

  const removeWord = (word: string) => {
    onChange(value.filter((w) => w !== word));
  };

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addWord();
            }
          }}
          placeholder={placeholder}
          className={inputCls}
        />
        <button
          type="button"
          onClick={addWord}
          title="Afegir paraula"
          className="shrink-0 flex items-center justify-center w-8 h-8 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Cap paraula afegida.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {value.map((word) => (
            <span
              key={word}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs"
            >
              {word}
              <button
                type="button"
                onClick={() => removeWord(word)}
                title={`Eliminar "${word}"`}
                className="p-0.5 rounded-full hover:bg-rose-200 text-rose-500 hover:text-rose-700 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Esborrany d'edició d'un grup: les llistes ES/CA i regions es mostren com a text (separades per
// comes), i els números com a text lliure, igual que la resta de camps d'aquest formulari.
interface KeywordGroupDraft {
  name: string;
  esText: string;
  caText: string;
  importMin: string;
  importMax: string;
  regionsText: string;
  proximityMaxHours: string;
  solvencyEconomic: string;
  solvencyTechnical: string;
  adjudicationMinPriceWeight: string;
  notes: string;
}

function groupsToDrafts(groups?: KeywordGroup[]): KeywordGroupDraft[] {
  return (groups || []).map((g) => ({
    name: g.name,
    esText: listToText(g.es),
    caText: listToText(g.ca),
    importMin: g.importMin != null ? String(g.importMin) : '',
    importMax: g.importMax != null ? String(g.importMax) : '',
    regionsText: listToText(g.proximityRegions),
    proximityMaxHours: g.proximityMaxHours != null ? String(g.proximityMaxHours) : '',
    solvencyEconomic: g.companySolvencyEconomic != null ? String(g.companySolvencyEconomic) : '',
    solvencyTechnical: g.companySolvencyTechnical != null ? String(g.companySolvencyTechnical) : '',
    adjudicationMinPriceWeight: g.adjudicationMinPriceWeight != null ? String(g.adjudicationMinPriceWeight) : '',
    notes: g.notes || '',
  }));
}

function draftsToGroups(drafts: KeywordGroupDraft[]): KeywordGroup[] {
  return drafts
    .map((d) => ({
      name: d.name.trim(),
      es: textToList(d.esText),
      ca: textToList(d.caText),
      importMin: d.importMin !== '' ? parseFloat(d.importMin) : null,
      importMax: d.importMax !== '' ? parseFloat(d.importMax) : null,
      proximityRegions: textToList(d.regionsText),
      proximityMaxHours: d.proximityMaxHours !== '' ? parseInt(d.proximityMaxHours, 10) : null,
      companySolvencyEconomic: d.solvencyEconomic !== '' ? parseFloat(d.solvencyEconomic) : null,
      companySolvencyTechnical: d.solvencyTechnical !== '' ? parseFloat(d.solvencyTechnical) : null,
      adjudicationMinPriceWeight: d.adjudicationMinPriceWeight !== '' ? parseFloat(d.adjudicationMinPriceWeight) : null,
      notes: d.notes.trim() || null,
    }))
    .filter((g) => g.name || g.es.length > 0 || g.ca.length > 0);
}

export default function TenderCriteriaConfig() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [keywordGroups, setKeywordGroups] = useState<KeywordGroupDraft[]>([]);
  const [exclusionKeywordsCa, setExclusionKeywordsCa] = useState<string[]>([]);
  const [exclusionKeywordsEs, setExclusionKeywordsEs] = useState<string[]>([]);
  const [executionMaxMonths, setExecutionMaxMonths] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: CriteriaConfigData }>('/tender-criteria/config');
        const c = res.data;
        setKeywordGroups(groupsToDrafts(c.adequacyKeywordGroups));
        setExclusionKeywordsCa((c.exclusionKeywordsCa || []).slice().sort((a, b) => a.localeCompare(b, 'ca')));
        setExclusionKeywordsEs((c.exclusionKeywordsEs || []).slice().sort((a, b) => a.localeCompare(b, 'es')));
        setExecutionMaxMonths(c.executionMaxMonths != null ? String(c.executionMaxMonths) : '');
      } catch (err) {
        console.error('[TenderCriteriaConfig] Error carregant configuració:', err);
        setError('Error en carregar la configuració de criteris');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const addKeywordGroup = () => {
    setKeywordGroups((prev) => [
      ...prev,
      {
        name: '', esText: '', caText: '',
        importMin: '', importMax: '',
        regionsText: '', proximityMaxHours: '',
        solvencyEconomic: '', solvencyTechnical: '',
        adjudicationMinPriceWeight: '',
        notes: '',
      },
    ]);
  };
  const updateKeywordGroup = (index: number, patch: Partial<KeywordGroupDraft>) => {
    setKeywordGroups((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };
  const removeKeywordGroup = (index: number) => {
    setKeywordGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const payload: CriteriaConfigData = {
        adequacyKeywordGroups: draftsToGroups(keywordGroups),
        exclusionKeywordsCa: [...exclusionKeywordsCa].sort((a, b) => a.localeCompare(b, 'ca')),
        exclusionKeywordsEs: [...exclusionKeywordsEs].sort((a, b) => a.localeCompare(b, 'es')),
        executionMaxMonths: executionMaxMonths !== '' ? parseInt(executionMaxMonths, 10) : null,
      };

      await api.put('/tender-criteria/config', payload);
      setSuccess(true);
    } catch (err: any) {
      console.error('[TenderCriteriaConfig] Error guardant configuració:', err);
      setError(err.response?.data?.error?.message || 'Error en guardar la configuració');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions')); navigate('/crm'); }}
            className="p-1.5 rounded hover:bg-gray-100"
          >
            <X size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Criteris de valoració de licitacions</h1>
            <p className="text-xs text-muted-foreground">Configuració general per decidir si interessa presentar-se a una licitació</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions')); navigate('/crm'); }}
            className="px-3 py-1.5 border border-gray-200 rounded text-xs font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel·lar
          </button>
          <button
            type="submit"
            form="tender-criteria-form"
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Desant...' : 'Desa'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <span>Configuració guardada correctament</span>
          </div>
        )}

        <form id="tender-criteria-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="space-y-6">
              {/* Criteri 1: Adequació — cada grup porta a més els seus propis criteris d'import,
                  proximitat, solvència i adjudicació. */}
              <CollapsibleCard title="Adequació de l'objecte i òrgan contractant" icon={ListChecks} iconClassName="text-blue-600" count={keywordGroups.length} defaultOpen>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={labelCls}>Grups de paraules clau (amb variants català / castellà)</label>
                      <button
                        type="button"
                        onClick={addKeywordGroup}
                        className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" /> Afegir grup
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Es coincideix si l'objecte de la licitació conté qualsevol paraula clau, en qualsevol dels dos idiomes.
                      Cada grup representa un tipus de producte o servei (p. ex. "Museu") i té els seus propis criteris
                      d'import, proximitat, solvència i adjudicació — les paraules d'exclusió i el termini d'execució
                      (a la dreta) són comuns a tots els grups i sempre hi prevalen per sobre.
                    </p>
                    {keywordGroups.length === 0 && (
                      <p className="text-sm text-muted-foreground italic mb-2">Encara no hi ha cap grup configurat.</p>
                    )}
                    <div className="space-y-3">
                      {keywordGroups.map((group, index) => (
                        <CollapsibleCard
                          key={index}
                          title={group.name || `Grup ${index + 1} (sense nom)`}
                          icon={ListChecks}
                          iconClassName="text-blue-500"
                          defaultOpen={!group.name}
                          className="bg-gray-50"
                          headerActions={
                            <button
                              type="button"
                              onClick={() => removeKeywordGroup(index)}
                              title="Eliminar grup"
                              className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          }
                        >
                        <div className="p-3 space-y-2">
                          <div>
                            <label className={labelCls}>Nom del grup</label>
                            <input
                              type="text"
                              value={group.name}
                              onChange={(e) => updateKeywordGroup(index, { name: e.target.value })}
                              placeholder="Nom del grup (ex: Museu)"
                              className={`${inputCls} bg-white font-medium`}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Català (separades per comes)</label>
                              <input
                                type="text"
                                value={group.caText}
                                onChange={(e) => updateKeywordGroup(index, { caText: e.target.value })}
                                placeholder="museu, museus, museogràfic..."
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Castellà (separades per comes)</label>
                              <input
                                type="text"
                                value={group.esText}
                                onChange={(e) => updateKeywordGroup(index, { esText: e.target.value })}
                                placeholder="museo, museos, museográfico..."
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                          </div>

                          {/* Import — propi d'aquest grup */}
                          <div className={subheaderCls}><Euro className="h-3.5 w-3.5 text-green-600" /> Import de la licitació</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Import mínim (€)</label>
                              <MoneyInput
                                value={group.importMin}
                                onValueChange={(v) => updateKeywordGroup(index, { importMin: v })}
                                zeroOnClear={false}
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Import màxim (€)</label>
                              <MoneyInput
                                value={group.importMax}
                                onValueChange={(v) => updateKeywordGroup(index, { importMax: v })}
                                zeroOnClear={false}
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                          </div>

                          {/* Proximitat — pròpia d'aquest grup */}
                          <div className={subheaderCls}><MapPin className="h-3.5 w-3.5 text-teal-600" /> Proximitat geogràfica</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Regions/CCAA acceptades (separades per comes)</label>
                              <input
                                type="text"
                                value={group.regionsText}
                                onChange={(e) => updateKeywordGroup(index, { regionsText: e.target.value })}
                                placeholder="Catalunya, Aragó, Comunitat Valenciana..."
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Desplaçament màxim (hores, informatiu)</label>
                              <input
                                type="number"
                                min="0"
                                value={group.proximityMaxHours}
                                onChange={(e) => updateKeywordGroup(index, { proximityMaxHours: e.target.value })}
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                          </div>

                          {/* Solvència — pròpia d'aquest grup */}
                          <div className={subheaderCls}><ShieldCheck className="h-3.5 w-3.5 text-purple-600" /> Solvència de l'empresa</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Solvència econòmica real (€)</label>
                              <MoneyInput
                                value={group.solvencyEconomic}
                                onValueChange={(v) => updateKeywordGroup(index, { solvencyEconomic: v })}
                                zeroOnClear={false}
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Solvència tècnica real (€)</label>
                              <MoneyInput
                                value={group.solvencyTechnical}
                                onValueChange={(v) => updateKeywordGroup(index, { solvencyTechnical: v })}
                                zeroOnClear={false}
                                className={`${inputCls} bg-white`}
                              />
                            </div>
                          </div>

                          {/* Criteris d'adjudicació — propis d'aquest grup */}
                          <div className={subheaderCls}><Award className="h-3.5 w-3.5 text-amber-600" /> Criteris d'adjudicació</div>
                          <div>
                            <label className={labelCls}>Pes mínim acceptable del preu (%, informatiu)</label>
                            <MoneyInput
                              value={group.adjudicationMinPriceWeight}
                              onValueChange={(v) => updateKeywordGroup(index, { adjudicationMinPriceWeight: v })}
                              zeroOnClear={false}
                              className={`${inputCls} bg-white md:w-1/2`}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Aquest criteri es mostra sempre com a "revisió manual" a la licitació, ja que el text dels
                              criteris d'adjudicació s'extreu en brut i requereix valoració humana.
                            </p>
                          </div>

                          {/* Notes — pròpies d'aquest grup */}
                          <div className={subheaderCls}><StickyNote className="h-3.5 w-3.5 text-gray-600" /> Notes</div>
                          <textarea
                            value={group.notes}
                            onChange={(e) => updateKeywordGroup(index, { notes: e.target.value })}
                            rows={2}
                            className={`${inputCls} bg-white resize-none`}
                            placeholder="Observacions sobre aquest tipus de producte/servei..."
                          />
                        </div>
                        </CollapsibleCard>
                      ))}
                    </div>
                  </div>
                </div>
              </CollapsibleCard>
            </div>

            <div className="space-y-6">
              {/* Paraules clau d'exclusió — comunes a tots els grups, sempre prevalen per sobre dels
                  criteris propis de cada grup. */}
              <CollapsibleCard title="Paraules clau d'exclusió" icon={Ban} iconClassName="text-rose-600" defaultOpen>
                <div className="p-5">
                  <label className={labelCls}>
                    Si l'objecte conté alguna d'aquestes paraules, la licitació es descarta automàticament
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Comuna a tots els grups: guanya sempre, encara que l'objecte també coincideixi amb un grup
                    d'adequació. S'aplica als avisos nous d'Infoconcurso: la licitació ni tan sols apareix com a
                    candidata per revisar, es desa directament en estat Descartada.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Català</label>
                      <KeywordChipsInput
                        value={exclusionKeywordsCa}
                        onChange={setExclusionKeywordsCa}
                        placeholder="ascensor, piscina municipal, gespa artificial..."
                        locale="ca"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Castellà</label>
                      <KeywordChipsInput
                        value={exclusionKeywordsEs}
                        onChange={setExclusionKeywordsEs}
                        placeholder="ascensor, piscina municipal, césped artificial..."
                        locale="es"
                      />
                    </div>
                  </div>
                </div>
              </CollapsibleCard>

              {/* Termini d'execució — comú a tots els grups. */}
              <CollapsibleCard title="Termini d'execució" icon={CalendarClock} iconClassName="text-orange-600">
                <div className="p-5">
                  <label className={labelCls}>Termini màxim acceptable (mesos)</label>
                  <p className="text-xs text-muted-foreground mb-2">Comú a tots els grups.</p>
                  <input
                    type="number"
                    min="0"
                    value={executionMaxMonths}
                    onChange={(e) => setExecutionMaxMonths(e.target.value)}
                    className={`${inputCls} md:w-1/2`}
                  />
                </div>
              </CollapsibleCard>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
